/**
 * A shared SCHEDULE PROJECTION — ordered phases, milestones and forecast events
 * rendered as a timeline, a table or an accessible list (#1466 recipe 5, ruling
 * 48362).
 *
 * ── what this is, and the thing it is deliberately NOT ──────────────────────
 * It is a PROJECTION, not a chart library. It takes dated facts a host already
 * holds and lays them on a period axis; it fetches nothing, owns no canvas,
 * invents no axis of its own and pulls in no rendering dependency. The whole
 * visual is CSS grid over a list of periods, which is why the same data can be
 * re-expressed as a table or a list without a second implementation.
 *
 * The composition exists because the donor product never had one. #1370 comment
 * 48368 measured the state of the art: "There is no coherent whole-quote
 * Schedule canvas... schedule behaviour is split across the line modal, pricing
 * drawer and an API with no quote-page consumer." Three half-views and an
 * endpoint nobody called. This is the whole that was missing, and it is GENERIC
 * on purpose — ramps, milestones, proration and previewed billing are one
 * adopter's mapping; projects, tasks, renewals and service commitments map
 * their own facts onto the same five fields.
 *
 * ── CONTROLLED, and owning no ambient state ─────────────────────────────────
 * `scale`/`onScaleChange` and `view`/`onViewChange`, and that is the whole of
 * it. This component reads no route, no `localStorage`, no global context and
 * no clock. It cannot: a page may show two schedules at once (a plan beside its
 * revision), so anything ambient would silently join two lenses that are meant
 * to be independent — and a component that read `Date.now()` would render
 * differently on every run, which is a projection you cannot write a test for.
 *
 * ── the five decisions this file is answerable for ──────────────────────────
 *
 * 1. THE ACCESSIBLE LIST IS NOT A DEGRADED MODE. It is a first-class `view`,
 *    always present in the switcher, carrying the SAME facts in a form a screen
 *    reader and a narrow viewport can both use. There is deliberately no
 *    breakpoint that substitutes it: a form of the data that appears only when
 *    you shrink the window is unreachable by exactly the reader most likely to
 *    need it — one on a full-size screen with a screen reader. Narrow viewports
 *    scroll the timeline instead, and the choice stays with the person.
 *
 * 2. A FORECAST IS NOT A FACT. Every entry declares `certainty`, and a forecast
 *    is marked in THREE channels — a word in the visible row, a word in the
 *    accessible name, and a dashed edge — because rendering a predicted invoice
 *    identically to an issued one is a lie the reader cannot detect, and colour
 *    is the one channel a reader may not have. The totals never merge the two
 *    into a single number for the same reason: one figure that is part fact and
 *    part guess is the same lie with a bigger blast radius.
 *
 * 3. PROVENANCE TRAVELS WITH EACH ENTRY. `provenance.source` is REQUIRED, not
 *    optional, and the type is where that is enforced — an optional field is a
 *    field that is absent in production. Every view carries it: a column in the
 *    table, a clause in the list sentence, and part of the bar's accessible
 *    name in the timeline. "Where did this date come from" is the first
 *    question asked of any schedule that surprises somebody.
 *
 * 4. TOTALS ARE A PROJECTION OF WHAT IS SHOWN, AND THEY SAY WHAT THEY OMIT. A
 *    fine scale over a long horizon would need thousands of period columns, so
 *    the projection caps them (`maxBuckets`) — and every entry the cap, the
 *    range or the capability model removes is COUNTED and NAMED underneath the
 *    figures. A total that silently disagrees with the rows above it is the
 *    defect; a total that admits "three entries beyond this window are not
 *    counted" is a projection you can trust.
 *
 * 5. EMPTY AND PARTIAL ARE DIFFERENT FACTS, AND SO IS REFUSED. "Nothing is
 *    scheduled", "we could not read part of it" and "you may not see this" are
 *    three different sentences and a governed surface must not collapse them
 *    into one blank box. `ScheduleLensState` makes the set finite so a host
 *    cannot invent a seventh, and an empty projection says WHY it is empty —
 *    nothing scheduled, nothing in range, nothing visible to you — rather than
 *    leaving the reader to guess which.
 *
 * ── capability filtering is PRESENTATION, not an authorisation boundary ─────
 * An entry may declare a `capability`; entries whose capability the caller does
 * not hold are not rendered, and their COUNT is disclosed under the totals.
 * Disclosing it is the deliberate call: a total that is silently short by an
 * unknown amount is undetectable, and "some of this is withheld from you" is
 * the honest thing to say to someone reading a number. Where the EXISTENCE of
 * an entry is itself confidential, the host must not pass the entry at all —
 * this filter is a lens on data the caller was already given, and the real
 * boundary is the read that produced it.
 *
 * `permitted` of `null` means "this host has no capability model" and shows
 * everything. It does not mean "nothing is permitted": that conflation is how a
 * portal ends up blank for every user the day somebody forgets to pass a list.
 *
 * ── an entry that cannot be placed is reported, never dropped ───────────────
 * An unreadable date, or an end before its start, makes an entry unplaceable.
 * It is counted as unplaced with its reason instead of vanishing, because a row
 * that disappears without a word is how a schedule quietly loses a milestone.
 */
import { useMemo, type KeyboardEvent } from 'react'
import { readableError } from '@solum/format'
import s from './ScheduleLens.module.css'

/* What a refusal reads as when the host's own words were machine text (#3012). */
const REFUSED = 'You don’t have access to this.'

/* ── the ruled numbers ─────────────────────────────────────────────────────── */

/**
 * The most period columns the projection will lay out.
 *
 * Not a taste decision: `day` scale across a three-year programme is over a
 * thousand grid columns, every one of them at least 72px, and the browser pays
 * for all of it to show a reader a strip they will scroll past. The cap is what
 * makes "the scale hides entries" a real, bounded thing — and everything it
 * hides is counted under the totals rather than dropped.
 */
export const SCHEDULE_MAX_BUCKETS = 60

/* ── the domain ────────────────────────────────────────────────────────────── */

export type ScheduleScale = 'day' | 'week' | 'month' | 'quarter'

/** The list is a peer of the other two, not a fallback the component chooses. */
export type ScheduleView = 'timeline' | 'table' | 'list'

/** A settled fact, or a prediction. There is no third value and no default. */
export type ScheduleCertainty = 'settled' | 'forecast'

/** What kind of thing this is on the axis. A milestone is an instant. */
export type ScheduleEntryKind = 'phase' | 'milestone' | 'event'

/** Where this date came from. Required — see decision 3 in the header. */
export interface ScheduleProvenance {
  /** In the host's own words: "signed order line", "derived from ramp". */
  source: string
  /** When that source last spoke, if the host knows. */
  asOf?: string
}

/** A quantity a schedule entry carries. `unit` is opaque — the host formats. */
export interface ScheduleQuantity {
  value: number
  /** A currency code, "days", "seats" — whatever the host is counting. */
  unit: string
}

export interface ScheduleEntry {
  /** Stable key. Also the token `onEntrySelect` reports. */
  key: string
  label: string
  kind: ScheduleEntryKind
  /** `YYYY-MM-DD`, or any ISO string beginning with one. */
  start: string
  /** Absent for a milestone. Must not precede `start`. */
  end?: string
  certainty: ScheduleCertainty
  provenance: ScheduleProvenance
  amount?: ScheduleQuantity
  /** Checked against `permitted`. Omitted = always visible. */
  capability?: string
}

/**
 * The lens body, made finite so a host cannot invent a seventh state.
 *
 * `partial` carries entries AND a message: the half that arrived is still worth
 * showing, and saying "we could not read the rest" beside it is what stops the
 * reader treating a short schedule as a complete one.
 */
export type ScheduleLensState =
  | { kind: 'loading' }
  | { kind: 'ready'; entries: readonly ScheduleEntry[] }
  | { kind: 'empty'; message: string }
  | { kind: 'partial'; entries: readonly ScheduleEntry[]; message: string }
  /** Authorisation said no to the whole projection. Not empty. */
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }

/** Identity for accessibility and tests. NOT a persistence key. */
export interface ScheduleSubject {
  id: string
  label: string
}

/* ── the pure projection ───────────────────────────────────────────────────── */

const DAY_MS = 86_400_000

/** Why an entry could not be laid on the axis. */
export type UnplacedReason = 'unreadable date' | 'ends before it starts'

export interface UnplacedEntry {
  entry: ScheduleEntry
  reason: UnplacedReason
}

export interface SchedulePeriod {
  /** Stable key: `2026-03-04`, `2026-W10`, `2026-03`, `2026-Q1`. */
  key: string
  /** UTC midnight at the period's first day. */
  startMs: number
}

export interface PlacedEntry {
  entry: ScheduleEntry
  /** Index into `periods` of the first period this entry touches. */
  from: number
  /** Index of the last period it touches. Equal to `from` for an instant. */
  to: number
}

export interface ScheduleAmountTotal {
  unit: string
  settled: number
  forecast: number
}

/** Nothing to show, and the reason — because three blanks are three facts. */
export type EmptyReason =
  | 'nothing scheduled'
  | 'nothing in range'
  | 'nothing visible to you'
  | 'nothing could be placed'

export interface ScheduleProjection {
  periods: readonly SchedulePeriod[]
  placed: readonly PlacedEntry[]
  unplaced: readonly UnplacedEntry[]
  /** Entries the range excluded. */
  outOfRange: number
  /** Entries the period cap excluded — the scale hiding things. */
  beyondScale: number
  /** Entries the capability model removed. */
  withheld: number
  /** Per unit, settled and forecast kept apart. Never summed together. */
  amounts: readonly ScheduleAmountTotal[]
  /** Set only when `placed` is empty. */
  emptyReason: EmptyReason | null
}

/** Parses the date part of an ISO string to UTC midnight, or `null`. */
export function parseScheduleDay(iso: string | undefined): number | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const year = Number(m[1]); const month = Number(m[2]); const day = Number(m[3])
  const ms = Date.UTC(year, month - 1, day)
  const back = new Date(ms)
  /* Round-tripped, so `2026-02-31` is rejected rather than rolling into March.
     A date that silently moves is worse than a date reported unreadable. */
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1
    || back.getUTCDate() !== day) return null
  return ms
}

/** ISO-8601 week numbering: the week owning the Thursday owns the year. */
function isoWeek(ms: number): { year: number; week: number } {
  const dow = (new Date(ms).getUTCDay() + 6) % 7          // Monday = 0
  const thursday = ms + (3 - dow) * DAY_MS
  const year = new Date(thursday).getUTCFullYear()
  const jan1 = Date.UTC(year, 0, 1)
  return { year, week: Math.floor((thursday - jan1) / (7 * DAY_MS)) + 1 }
}

/** UTC midnight at the start of the period containing `ms`. */
export function periodStart(ms: number, scale: ScheduleScale): number {
  const d = new Date(ms)
  const y = d.getUTCFullYear(); const m = d.getUTCMonth()
  switch (scale) {
    case 'day': return Date.UTC(y, m, d.getUTCDate())
    case 'week': return Date.UTC(y, m, d.getUTCDate()) - ((d.getUTCDay() + 6) % 7) * DAY_MS
    case 'month': return Date.UTC(y, m, 1)
    case 'quarter': return Date.UTC(y, Math.floor(m / 3) * 3, 1)
  }
}

/** The period key a reader sees on the axis. Locale-free by design. */
export function periodKey(ms: number, scale: ScheduleScale): string {
  const d = new Date(periodStart(ms, scale))
  const y = d.getUTCFullYear()
  const pad = (n: number) => String(n).padStart(2, '0')
  switch (scale) {
    case 'day': return `${y}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
    case 'week': { const w = isoWeek(d.getTime()); return `${w.year}-W${pad(w.week)}` }
    case 'month': return `${y}-${pad(d.getUTCMonth() + 1)}`
    case 'quarter': return `${y}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`
  }
}

function nextPeriod(startMs: number, scale: ScheduleScale): number {
  const d = new Date(startMs)
  switch (scale) {
    case 'day': return startMs + DAY_MS
    case 'week': return startMs + 7 * DAY_MS
    case 'month': return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)
    case 'quarter': return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 3, 1)
  }
}

/** Entries this caller may see. `null` permitted = no capability model at all. */
export function permittedEntries(
  entries: readonly ScheduleEntry[], permitted: readonly string[] | null | undefined,
): readonly ScheduleEntry[] {
  if (permitted == null) return entries
  const held = new Set(permitted)
  return entries.filter((e) => !e.capability || held.has(e.capability))
}

export interface ProjectScheduleOptions {
  scale: ScheduleScale
  permitted?: readonly string[] | null
  range?: { from?: string; to?: string } | null
  maxBuckets?: number
}

/**
 * The whole projection, as a pure function of its inputs.
 *
 * Pure because this is the part worth testing hardest and the part a second
 * adopter reads before trusting the lens. Nothing here touches the DOM, a clock
 * or a locale, so the same inputs give the same periods, the same order and the
 * same totals on any machine on any day.
 */
export function projectSchedule(
  entries: readonly ScheduleEntry[], options: ProjectScheduleOptions,
): ScheduleProjection {
  const { scale, permitted = null, range = null, maxBuckets = SCHEDULE_MAX_BUCKETS } = options

  const visible = permittedEntries(entries, permitted)
  const withheld = entries.length - visible.length

  const rangeFrom = parseScheduleDay(range?.from)
  const rangeTo = parseScheduleDay(range?.to)

  const unplaced: UnplacedEntry[] = []
  const dated: { entry: ScheduleEntry; startMs: number; endMs: number }[] = []
  let outOfRange = 0

  for (const entry of visible) {
    const startMs = parseScheduleDay(entry.start)
    /* An absent `end` is an instant, which is what a milestone is. An `end`
       that is present and unreadable is a defect, not an instant — reporting it
       is how the host finds out. */
    const endMs = entry.end === undefined ? startMs : parseScheduleDay(entry.end)
    if (startMs === null || endMs === null) { unplaced.push({ entry, reason: 'unreadable date' }); continue }
    if (endMs < startMs) { unplaced.push({ entry, reason: 'ends before it starts' }); continue }
    if (rangeFrom !== null && endMs < rangeFrom) { outOfRange += 1; continue }
    if (rangeTo !== null && startMs > rangeTo) { outOfRange += 1; continue }
    dated.push({ entry, startMs, endMs })
  }

  /* Ordered by when they begin, then by label, so two entries starting on the
     same day do not swap places between renders of identical data. */
  dated.sort((a, b) => a.startMs - b.startMs || a.entry.label.localeCompare(b.entry.label))

  const periods: SchedulePeriod[] = []
  const index = new Map<number, number>()
  let beyondScale = 0
  const placed: PlacedEntry[] = []

  if (dated.length > 0) {
    const firstMs = Math.min(...dated.map((d) => d.startMs))
    const lastMs = Math.max(...dated.map((d) => d.endMs))
    let cursor = periodStart(firstMs, scale)
    const lastStart = periodStart(lastMs, scale)
    while (cursor <= lastStart && periods.length < maxBuckets) {
      index.set(cursor, periods.length)
      periods.push({ key: periodKey(cursor, scale), startMs: cursor })
      cursor = nextPeriod(cursor, scale)
    }
    const horizon = periods[periods.length - 1]?.startMs ?? -Infinity

    for (const d of dated) {
      const from = index.get(periodStart(d.startMs, scale))
      /* Begins past the last period the cap allowed: the SCALE hid it, and the
         totals will say so rather than quietly under-counting. */
      if (from === undefined) { beyondScale += 1; continue }
      const rawTo = index.get(periodStart(d.endMs, scale))
      /* It begins inside the window and runs past it. Clamped to the last
         period rather than excluded — it is on screen, so it counts. */
      const to = rawTo ?? (periodStart(d.endMs, scale) > horizon ? periods.length - 1 : from)
      placed.push({ entry: d.entry, from, to: Math.max(from, to) })
    }
  }

  const amounts: ScheduleAmountTotal[] = []
  for (const p of placed) {
    const amount = p.entry.amount
    if (!amount) continue
    let row = amounts.find((a) => a.unit === amount.unit)
    if (!row) { row = { unit: amount.unit, settled: 0, forecast: 0 }; amounts.push(row) }
    /* Settled and forecast accumulate into different fields and are never added
       together. See decision 2 — a single figure that is part fact and part
       guess cannot be un-mixed by the reader. */
    if (p.entry.certainty === 'forecast') row.forecast += amount.value
    else row.settled += amount.value
  }

  const emptyReason: EmptyReason | null = placed.length > 0 ? null
    : entries.length === 0 ? 'nothing scheduled'
      : visible.length === 0 ? 'nothing visible to you'
        : outOfRange > 0 || beyondScale > 0 ? 'nothing in range'
          : unplaced.length > 0 ? 'nothing could be placed'
            : 'nothing scheduled'

  return { periods, placed, unplaced, outOfRange, beyondScale, withheld, amounts, emptyReason }
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * What the totals are NOT counting, in sentences.
 *
 * Separate and exported because it is the assertion that keeps decision 4
 * honest: if the figures and the rows can disagree, the disagreement has to be
 * printable, and a caveat list nobody can call is a caveat list nobody renders.
 */
export function totalsCaveats(projection: ScheduleProjection): readonly string[] {
  const out: string[] = []
  const is = (n: number) => (n === 1 ? 'is' : 'are')
  if (projection.outOfRange > 0) {
    out.push(`${plural(projection.outOfRange, 'entry falls', 'entries fall')} outside this range and ${is(projection.outOfRange)} not counted.`)
  }
  if (projection.beyondScale > 0) {
    out.push(`${plural(projection.beyondScale, 'entry falls', 'entries fall')} beyond the ${projection.periods.length} periods this scale can show and ${is(projection.beyondScale)} not counted.`)
  }
  if (projection.withheld > 0) {
    out.push(`${plural(projection.withheld, 'entry is', 'entries are')} withheld from you and not counted.`)
  }
  if (projection.unplaced.length > 0) {
    out.push(`${plural(projection.unplaced.length, 'entry could', 'entries could')} not be placed and ${is(projection.unplaced.length)} not counted.`)
  }
  return out
}

const EMPTY_TEXT: Record<EmptyReason, string> = {
  'nothing scheduled': 'Nothing is scheduled.',
  'nothing in range': 'Nothing is scheduled in this range at this scale.',
  'nothing visible to you': 'No schedule entries are visible to you.',
  'nothing could be placed': 'No entry could be placed on the schedule.',
}

/* ── the sentence every view shares ────────────────────────────────────────── */

export interface DescribeOptions {
  formatDate: (iso: string) => string
  formatAmount: (value: number, unit: string) => string
}

const CERTAINTY_WORD: Record<ScheduleCertainty, string> = {
  settled: 'Settled',
  forecast: 'Forecast',
}

/**
 * One entry as a full sentence — the accessible name in the timeline, the item
 * text in the list, and the thing a test can assert once for all three views.
 *
 * Certainty and provenance are unconditional clauses. Not "if the host set
 * them": they are the two facts this recipe exists to stop a schedule losing,
 * and a clause that is sometimes omitted is a clause whose absence the reader
 * cannot interpret.
 */
export function describeEntry(entry: ScheduleEntry, options: DescribeOptions): string {
  const when = entry.end && entry.end !== entry.start
    ? `${options.formatDate(entry.start)} to ${options.formatDate(entry.end)}`
    : options.formatDate(entry.start)
  const parts = [`${entry.label}, ${entry.kind}`, when, CERTAINTY_WORD[entry.certainty]]
  if (entry.amount) parts.push(options.formatAmount(entry.amount.value, entry.amount.unit))
  parts.push(entry.provenance.asOf
    ? `Source: ${entry.provenance.source}, as at ${options.formatDate(entry.provenance.asOf)}`
    : `Source: ${entry.provenance.source}`)
  return `${parts.join('. ')}.`
}

/* ── the component ─────────────────────────────────────────────────────────── */

export interface ScheduleLensProps {
  state: ScheduleLensState
  subject: ScheduleSubject
  /** Controlled. The host decides the axis granularity. */
  scale: ScheduleScale
  onScaleChange: (scale: ScheduleScale) => void
  /** Controlled. The list is one of these, never a substitution. */
  view: ScheduleView
  onViewChange: (view: ScheduleView) => void
  /** Which scales this host offers. Defaults to all four. */
  scales?: readonly ScheduleScale[]
  /** Capability keys the caller holds, or `null` for "no capability model". */
  permitted?: readonly string[] | null
  /** The window of interest. Entries wholly outside it are counted, not drawn. */
  range?: { from?: string; to?: string } | null
  maxBuckets?: number
  /** Locale belongs to the host; the default is the ISO string, unchanged. */
  formatDate?: (iso: string) => string
  formatAmount?: (value: number, unit: string) => string
  /** When given, entries become 44px-tall buttons reporting their key. */
  onEntrySelect?: (key: string) => void
  testId?: string
}

const ALL_SCALES: readonly ScheduleScale[] = ['day', 'week', 'month', 'quarter']
const ALL_VIEWS: readonly ScheduleView[] = ['timeline', 'table', 'list']

const SCALE_GLYPH: Record<ScheduleScale, string> = {
  day: '▪', week: '▬', month: '▮', quarter: '▰',
}

const SCALE_LABEL: Record<ScheduleScale, string> = {
  day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter',
}

const VIEW_GLYPH: Record<ScheduleView, string> = {
  timeline: '▤', table: '▦', list: '☰',
}

const VIEW_LABEL: Record<ScheduleView, string> = {
  timeline: 'Timeline', table: 'Table', list: 'List',
}

export function ScheduleLens({
  state, subject, scale, onScaleChange, view, onViewChange,
  scales = ALL_SCALES, permitted = null, range = null,
  maxBuckets = SCHEDULE_MAX_BUCKETS,
  formatDate = (iso) => iso,
  formatAmount = (value, unit) => `${value} ${unit}`,
  onEntrySelect, testId = 'schedule',
}: ScheduleLensProps) {
  const entries = state.kind === 'ready' || state.kind === 'partial' ? state.entries : null

  const projection = useMemo(
    () => (entries === null ? null : projectSchedule(entries, { scale, permitted, range, maxBuckets })),
    [entries, scale, permitted, range, maxBuckets])

  const describe: DescribeOptions = { formatDate, formatAmount }

  return (
    <section className={s.lens} data-testid={testId} data-state={state.kind} data-view={view}
      aria-label={`Schedule — ${subject.label}`}>
      {/* The controls stay mounted in EVERY state. A reader who arrives at an
          error or a refusal can still change scale and re-ask, and the List
          view is still one deliberate press away rather than appearing only
          once data happens to load. */}
      <div className={s.controls}>
        <Choice label="Scale" value={scale} onChange={onScaleChange} testId={`${testId}-scale`}
          options={scales.map((k) => ({ key: k, label: SCALE_LABEL[k], glyph: SCALE_GLYPH[k] }))} />
        <Choice label="View" value={view} onChange={onViewChange} testId={`${testId}-view`}
          options={ALL_VIEWS.map((k) => ({ key: k, label: VIEW_LABEL[k], glyph: VIEW_GLYPH[k] }))} />
      </div>

      {state.kind === 'loading' && (
        <p className={s.note} role="status" data-testid={`${testId}-note`}>Loading…</p>
      )}
      {state.kind === 'empty' && (
        <p className={s.note} data-kind="empty" data-testid={`${testId}-note`}>{state.message}</p>
      )}
      {state.kind === 'refused' && (
        /* Not an error and not empty. The reader is not missing a schedule;
           they are not permitted to see it, and saying which is the difference
           between a broken lens and a governed one. */
        <p className={s.note} data-kind="refused" role="status" data-testid={`${testId}-note`}>{readableError(state.message, REFUSED)}</p>
      )}
      {state.kind === 'error' && (
        <p className={s.note} data-kind="error" role="alert" data-testid={`${testId}-note`}>{readableError(state.message)}</p>
      )}
      {state.kind === 'partial' && (
        <p className={s.note} data-kind="partial" role="status" data-testid={`${testId}-note`}>{state.message}</p>
      )}

      {projection && (projection.placed.length === 0
        ? (
          <p className={s.note} data-testid={`${testId}-empty`} data-reason={projection.emptyReason ?? undefined}>
            {EMPTY_TEXT[projection.emptyReason ?? 'nothing scheduled']}
          </p>
        )
        : (
          <>
            {view === 'timeline' && (
              <TimelineView projection={projection} describe={describe} subject={subject}
                onEntrySelect={onEntrySelect} testId={testId} />
            )}
            {view === 'table' && (
              <TableView projection={projection} describe={describe} subject={subject}
                scale={scale} onEntrySelect={onEntrySelect} testId={testId} />
            )}
            {view === 'list' && (
              <ListView projection={projection} describe={describe} subject={subject}
                testId={testId} />
            )}
          </>
        ))}

      {projection && <Totals projection={projection} formatAmount={formatAmount} testId={testId} />}
    </section>
  )
}

/* ── controls ──────────────────────────────────────────────────────────────── */

interface ChoiceProps<T extends string> {
  label: string
  options: readonly { key: T; label: string; glyph: string }[]
  value: T
  onChange: (next: T) => void
  testId: string
}

/**
 * A radiogroup, not a row of toggle buttons.
 *
 * Scale and view are each one-of-N, and `role="radiogroup"` is the role that
 * says so: a screen reader announces "2 of 4", and the group is ONE tab stop
 * with arrows inside it. A row of `aria-pressed` buttons would put four tab
 * stops between the reader and the data and would never tell them how many
 * choices there were.
 */
function Choice<T extends string>({ label, options, value, onChange, testId }: ChoiceProps<T>) {
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End']
    if (!keys.includes(event.key)) return
    event.preventDefault()
    const at = options.findIndex((o) => o.key === value)
    const last = options.length - 1
    const next = event.key === 'Home' ? 0
      : event.key === 'End' ? last
        : event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? (at + 1) % options.length
          : (at + options.length - 1) % options.length
    const target = options[next]
    if (target) onChange(target.key)
  }

  return (
    <div className={s.choice} role="radiogroup" aria-label={label}
      onKeyDown={move} data-testid={testId}>
      <span className={s.choiceLabel} aria-hidden>{label}</span>
      {options.map((o) => (
        <button key={o.key} type="button" role="radio" className={s.opt}
          aria-checked={o.key === value}
          /* Roving tabindex: the group is one stop, and the stop is the current
             choice — so a keyboard reader returning to it lands where they left. */
          tabIndex={o.key === value ? 0 : -1}
          data-testid={`${testId}-${o.key}`}
          onClick={() => onChange(o.key)}>
          <span className={s.glyph} aria-hidden>{o.glyph}</span>
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* ── views ─────────────────────────────────────────────────────────────────── */

interface ViewProps {
  projection: ScheduleProjection
  describe: DescribeOptions
  subject: ScheduleSubject
  testId: string
  onEntrySelect?: (key: string) => void
}

function TimelineView({ projection, describe, subject, testId, onEntrySelect }: ViewProps) {
  const columns = { ['--sl-count' as string]: String(projection.periods.length) }
  return (
    <div className={s.scroll} data-testid={`${testId}-timeline`}>
      <div className={s.grid}>
        {/* The period strip is decoration for assistive technology: every fact
            it carries is already inside each bar's accessible name, and a
            screen reader reading sixty column headings before the first row is
            not being helped. */}
        <div className={s.periods} style={columns} aria-hidden>
          <span />
          {projection.periods.map((p) => <span key={p.key} className={s.period}>{p.key}</span>)}
        </div>
        <div role="list" aria-label={`Schedule entries — ${subject.label}`}>
          {projection.placed.map(({ entry, from, to }) => {
            const sentence = describeEntry(entry, describe)
            const span = { gridColumn: `${from + 2} / ${to + 3}` }
            return (
              <div key={entry.key} className={s.row} role="listitem" style={columns}>
                <span className={s.rowLabel} aria-hidden>{entry.label}</span>
                {onEntrySelect
                  ? (
                    <button type="button" className={s.bar} style={span}
                      data-kind={entry.kind} data-certainty={entry.certainty}
                      data-testid={`${testId}-entry-${entry.key}`}
                      aria-label={sentence} title={sentence}
                      onClick={() => onEntrySelect(entry.key)}>
                      <Mark certainty={entry.certainty} />
                      {entry.label}
                    </button>
                  )
                  : (
                    /* `role="img"` is load-bearing, not decoration. An
                       `aria-label` on a bare `<span>` is a name on an element
                       with no role, and assistive technology is free to ignore
                       it — which would leave the whole sentence, certainty and
                       provenance included, announced as nothing at all. A bar
                       IS an atomic graphic conveying one fact, so `img` is both
                       the honest role and the one that makes the name
                       authoritative. The interactive branch above needs none of
                       this: a button takes its name from the author already. */
                    <span className={s.bar} style={span} role="img"
                      data-kind={entry.kind} data-certainty={entry.certainty}
                      data-testid={`${testId}-entry-${entry.key}`}
                      aria-label={sentence} title={sentence}>
                      <Mark certainty={entry.certainty} />
                      {entry.label}
                    </span>
                  )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** The visible text channel for certainty. Never colour on its own. */
function Mark({ certainty }: { certainty: ScheduleCertainty }) {
  return <span className={s.mark} data-certainty={certainty}>{CERTAINTY_WORD[certainty]}</span>
}

function TableView({ projection, describe, subject, testId, onEntrySelect, scale }: ViewProps & { scale: ScheduleScale }) {
  return (
    <div className={s.scroll}>
      <table className={s.table} data-testid={`${testId}-table`}>
        <caption>{`Schedule — ${subject.label}, by ${scale}`}</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">Entry</th>
            <th scope="col">Kind</th>
            <th scope="col">Certainty</th>
            <th scope="col">Amount</th>
            <th scope="col">Source</th>
          </tr>
        </thead>
        <tbody>
          {projection.placed.map(({ entry, from, to }) => (
            <tr key={entry.key} data-certainty={entry.certainty}
              data-testid={`${testId}-row-${entry.key}`}>
              <td>{from === to
                ? projection.periods[from]?.key
                : `${projection.periods[from]?.key} – ${projection.periods[to]?.key}`}</td>
              <th scope="row">
                {onEntrySelect
                  ? (
                    <button type="button" className={s.opt} data-testid={`${testId}-entry-${entry.key}`}
                      aria-label={describeEntry(entry, describe)}
                      onClick={() => onEntrySelect(entry.key)}>{entry.label}</button>
                  )
                  : entry.label}
              </th>
              <td>{entry.kind}</td>
              {/* A column of its own, and a word rather than a tint: the table
                  is where a forecast is most likely to be read as a fact. */}
              <td>{CERTAINTY_WORD[entry.certainty]}</td>
              <td>{entry.amount ? describe.formatAmount(entry.amount.value, entry.amount.unit) : '—'}</td>
              <td className={s.source}>{entry.provenance.asOf
                ? `${entry.provenance.source} (as at ${describe.formatDate(entry.provenance.asOf)})`
                : entry.provenance.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The same facts, grouped by period, as sentences.
 *
 * Not a degraded timeline: every clause the bars carry in their accessible
 * names is here as readable text, so choosing List costs the reader nothing.
 * Grouping by period is what replaces the axis — the ordering IS the schedule.
 *
 * An entry that spans several periods is listed ONCE, under the period it
 * begins in, and its sentence carries the end date. Repeating it under every
 * period it touches would make a long phase look like several events, which is
 * a different schedule from the one the timeline is drawing.
 */
function ListView({ projection, describe, subject, testId }: ViewProps) {
  const groups = projection.periods
    .map((period, i) => ({ period, items: projection.placed.filter((p) => p.from === i) }))
    .filter((g) => g.items.length > 0)

  return (
    <div className={s.list} data-testid={`${testId}-list`}
      role="group" aria-label={`Schedule entries — ${subject.label}`}>
      {groups.map(({ period, items }) => (
        <section key={period.key}>
          <h3 className={s.listPeriod}>{period.key}</h3>
          <ul className={s.listItems}>
            {items.map(({ entry }) => (
              <li key={entry.key} data-testid={`${testId}-entry-${entry.key}`}
                data-certainty={entry.certainty}>{describeEntry(entry, describe)}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/* ── totals ────────────────────────────────────────────────────────────────── */

function Totals({ projection, formatAmount, testId }: {
  projection: ScheduleProjection
  formatAmount: (value: number, unit: string) => string
  testId: string
}) {
  const caveats = totalsCaveats(projection)
  if (projection.amounts.length === 0 && caveats.length === 0) return null
  return (
    <footer className={s.totals} data-testid={`${testId}-totals`}>
      {projection.amounts.length > 0 && (
        <p className={s.amounts}>
          {projection.amounts.map((a) => (
            /* Two figures, never their sum. A merged total is part fact and
               part prediction and the reader cannot tell which part moved. */
            <span key={a.unit} className={s.amount} data-testid={`${testId}-total-${a.unit}`}>
              <span className={s.amountUnit}>Settled</span>{formatAmount(a.settled, a.unit)}
              <span className={s.amountUnit}>Forecast</span>{formatAmount(a.forecast, a.unit)}
            </span>
          ))}
        </p>
      )}
      {caveats.length > 0 && (
        <ul className={s.caveats} data-testid={`${testId}-caveats`}>
          {caveats.map((c) => <li key={c}>{c}</li>)}
        </ul>
      )}
    </footer>
  )
}
